import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { downloadApiFile } from '../../api/download';
import { deleteFile, listFiles, replaceFileContent } from '../../api/endpoints';
import { getValidationFailures } from '../../api/http';
import { toApiPage, toApiSort, type GridPaginationModel } from '../../api/paging';
import { queryKeys } from '../../api/queryKeys';
import { fileKinds, fileSortKeys, type FileKind, type StoredFileListItem } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyableText } from '../../components/CopyableText';
import { ErrorBlock, GridEmptyOverlay } from '../../components/QueryStateOverlay';
import { FileKindChip } from '../../components/StatusChips';
import { ValidationFindingsDialog, type ValidationFindings } from '../../components/ValidationFindingsDialog';
import { fileKindLabelKeys, fileSourceLabelKeys } from '../../components/statusLabels';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { formatBytes, formatDateTime, shortHash } from '../../utils/format';
import { FileDetailDrawer } from './FileDetailDrawer';
import { UploadFileDialog } from './UploadFileDialog';

export function FilesPage() {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();
  // Nothing to query: an administrator has not picked a tenant yet, or the caller holds none.
  const hasNoTenant = selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedFileGid = searchParams.get('file');

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [searchInput, setSearchInput] = useState('');
  const [kinds, setKinds] = useState<FileKind[]>([]);
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<StoredFileListItem | null>(null);

  /** What the checks said about replacement content — why it was refused, or what to know about it. */
  const [findings, setFindings] = useState<ValidationFindings | null>(null);

  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<string | null>(null);

  const search = useDebouncedValue(searchInput);

  const filter = useMemo(
    () => ({
      page: toApiPage(paginationModel.page),
      pageSize: paginationModel.pageSize,
      ...toApiSort(sortModel, fileSortKeys),
      ...(kinds.length > 0 ? { kinds } : {}),
      ...(unusedOnly ? { inUse: false } : {}),
      ...(search ? { search } : {}),
    }),
    [paginationModel, sortModel, kinds, unusedOnly, search],
  );

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty list.
  const query = useQuery({
    queryKey: queryKeys.files.list(selectedTenantGid, filter),
    queryFn: ({ signal }) => listFiles(filter, signal),
    placeholderData: keepPreviousData,
    enabled: selectedTenantGid !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (fileGid: string) => deleteFile(fileGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.files.all });
      toastSuccess(t('files.deleted'));
      setPendingDelete(null);
    },
    onError: (error) => {
      toastError(error);
      setPendingDelete(null);
    },
  });

  const replaceMutation = useMutation({
    mutationFn: ({ fileGid, file }: { fileGid: string; file: File }) => replaceFileContent(fileGid, file),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.files.all });
      toastSuccess(t('files.replaced', { name: result.file.fileName }));

      // The content is in, but a finding nobody sees is one nobody acts on.
      if (result.warnings.length > 0)
        setFindings({
          severity: 'warning',
          message: t('files.validation.replacedWithWarnings'),
          items: result.warnings,
        });
    },
    onError: (error) => {
      const failures = getValidationFailures(error);
      if (failures.length > 0)
        setFindings({ severity: 'error', message: t('files.validation.replaceRejected'), items: failures });
      else toastError(error);
    },
  });

  const download = async (row: StoredFileListItem) => {
    try {
      await downloadApiFile(`/file/${row.fileGid}`, { fallbackFileName: row.fileName });
    } catch (error) {
      toastError(error);
    }
  };

  const openReplacePicker = (fileGid: string) => {
    replaceTargetRef.current = fileGid;
    replaceInputRef.current?.click();
  };

  const columns = useMemo<GridColDef<StoredFileListItem>[]>(
    () => [
      { field: 'fileName', headerName: t('files.columns.fileName'), flex: 1, minWidth: 220 },
      {
        field: 'kind',
        headerName: t('files.columns.kind'),
        width: 140,
        renderCell: (params) => <FileKindChip kind={params.row.kind} />,
      },
      {
        field: 'source',
        headerName: t('files.columns.source'),
        width: 120,
        valueGetter: (_value, row) => t(fileSourceLabelKeys[row.source]),
      },
      {
        field: 'sizeBytes',
        headerName: t('files.columns.size'),
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) => formatBytes(row.sizeBytes),
      },
      {
        field: 'sha256',
        headerName: t('files.columns.sha256'),
        width: 190,
        sortable: false,
        renderCell: (params) =>
          params.row.sha256 ? (
            <CopyableText
              value={params.row.sha256}
              label={shortHash(params.row.sha256)}
              title={params.row.sha256}
            />
          ) : (
            '—'
          ),
      },
      {
        field: 'createdAt',
        headerName: t('files.columns.created'),
        width: 170,
        valueGetter: (_value, row) => formatDateTime(row.createdAt),
      },
      {
        field: 'inUse',
        headerName: t('files.columns.inUse'),
        width: 110,
        sortable: false,
        renderCell: (params) =>
          params.row.inUse ? (
            <Tooltip
              title={t('files.referencesTooltip', {
                operations: params.row.operationRefCount,
                received: params.row.receivedRefCount,
              })}
            >
              <Chip size="small" color="warning" variant="outlined" label={t('files.inUseChip')} />
            </Tooltip>
          ) : (
            <Chip size="small" variant="outlined" label={t('files.unusedChip')} />
          ),
      },
      {
        field: 'actions',
        headerName: t('columns.actions'),
        width: 180,
        sortable: false,
        renderCell: (params) => {
          const { inUse } = params.row;
          return (
            <Stack direction="row" spacing={0.25}>
              <Tooltip title={t('actions.download')}>
                <IconButton size="small" onClick={() => void download(params.row)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('files.rowDetail')}>
                <IconButton
                  size="small"
                  onClick={() => setSearchParams({ file: params.row.fileGid }, { replace: false })}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* A disabled MUI button swallows pointer events, so the tooltip needs a live wrapper. */}
              <Tooltip title={inUse ? t('files.inUseExplanation') : t('files.replaceContent')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={inUse || replaceMutation.isPending}
                    onClick={() => openReplacePicker(params.row.fileGid)}
                  >
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={inUse ? t('files.inUseExplanation') : t('actions.delete')}>
                <span>
                  <IconButton size="small" disabled={inUse} onClick={() => setPendingDelete(params.row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // download/openReplacePicker are stable enough for a column definition rebuilt on these inputs.
    [replaceMutation.isPending, setSearchParams, t],
  );

  const noRowsOverlay = useCallback(
    () => <GridEmptyOverlay message={hasNoTenant ? t(noTenantKey) : t('files.empty')} />,
    [hasNoTenant, noTenantKey, t],
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5">{t('files.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('files.description')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title={hasNoTenant ? t(noTenantKey) : ''}>
            <span>
              {/* The send dialog lives on the operations page — a submission is an operation. */}
              <Button
                startIcon={<SendIcon />}
                variant="outlined"
                disabled={hasNoTenant}
                onClick={() => void navigate('/operations?send=1')}
              >
                {t('operations.send.send')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={hasNoTenant ? t(noTenantKey) : ''}>
            <span>
              <Button
                startIcon={<UploadIcon />}
                variant="contained"
                disabled={hasNoTenant}
                onClick={() => setUploadOpen(true)}
              >
                {t('actions.upload')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            label={t('files.searchLabel')}
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            sx={{ minWidth: 240 }}
          />
          <Select
            size="small"
            multiple
            displayEmpty
            value={kinds}
            onChange={(event) => {
              const value = event.target.value;
              setKinds(typeof value === 'string' ? [] : value);
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
            renderValue={(selected) =>
              selected.length === 0
                ? t('files.anyKind')
                : selected.map((kind) => t(fileKindLabelKeys[kind])).join(', ')
            }
            sx={{ minWidth: 220 }}
          >
            {fileKinds.map((kind) => (
              <MenuItem key={kind} value={kind}>
                <Checkbox checked={kinds.includes(kind)} size="small" />
                <ListItemText primary={t(fileKindLabelKeys[kind])} />
              </MenuItem>
            ))}
          </Select>
          <Tooltip title={t('files.unusedOnlyTooltip')}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={unusedOnly}
                  onChange={(event) => {
                    setUnusedOnly(event.target.checked);
                    setPaginationModel((current) => ({ ...current, page: 0 }));
                  }}
                />
              }
              label={t('files.unusedOnly')}
            />
          </Tooltip>
        </Stack>
      </Paper>

      {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

      <Paper sx={{ height: 620 }}>
        <DataGrid<StoredFileListItem>
          rows={hasNoTenant ? [] : (query.data?.items ?? [])}
          columns={columns}
          getRowId={(row) => row.fileGid}
          loading={query.isFetching}
          rowCount={hasNoTenant ? 0 : (query.data?.pagedMetadata.totalCount ?? 0)}
          paginationMode="server"
          sortingMode="server"
          disableColumnFilter
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ noRowsOverlay }}
        />
      </Paper>

      <input
        ref={replaceInputRef}
        type="file"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          const fileGid = replaceTargetRef.current;
          if (file && fileGid) replaceMutation.mutate({ fileGid, file });
          event.target.value = '';
          replaceTargetRef.current = null;
        }}
      />

      {uploadOpen && <UploadFileDialog onClose={() => setUploadOpen(false)} />}

      <FileDetailDrawer
        fileGid={selectedFileGid}
        onClose={() => {
          const next = new URLSearchParams(searchParams);
          next.delete('file');
          setSearchParams(next, { replace: true });
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('files.confirmDeleteTitle')}
        message={t('files.confirmDeleteMessage', { name: pendingDelete?.fileName ?? '' })}
        confirmLabel={t('actions.delete')}
        confirmColor="error"
        busy={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.fileGid)}
        onClose={() => setPendingDelete(null)}
      />

      {findings && <ValidationFindingsDialog findings={findings} onClose={() => setFindings(null)} />}
    </Stack>
  );
}
